/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\FaxDeviceTechnicalSettings.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Virtual Faxes module.
----------------------------------------------------------------*/


import { Settings } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import Select from '../../../components/ui/select';
import Toggle from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import type { FaxDeviceFormState } from '../types';

interface FaxDeviceTechnicalSettingsProps {
  formData: FaxDeviceFormState;
  onFieldChange: <K extends keyof FaxDeviceFormState>(field: K, value: FaxDeviceFormState[K]) => void;
  t: (key: string, fallback?: string) => string;
  resolutionOptions: Array<{ value: string; label: string }>;
  paperSizeOptions: Array<{ value: string; label: string }>;
}

export function FaxDeviceTechnicalSettings({
  formData, onFieldChange, t, resolutionOptions, paperSizeOptions,
}: FaxDeviceTechnicalSettingsProps) {
  return (
    <FormBox
      title={t('faxDevices:sections.technicalSettings')}
      icon={<Settings className="h-5 w-5" />}
      variant="elevated"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">

        {/* Resolution */}
        <SimpleTooltip content={t('faxDevices:hints.resolution')} title={t('faxDevices:fields.resolution')} position="top" maxWidth="400px">
          <Label htmlFor="resolution" className="text-left">{t('faxDevices:fields.resolution')}</Label>
        </SimpleTooltip>
        <Select value={formData.resolution} onValueChange={(v) => onFieldChange('resolution', v)} options={resolutionOptions} />

        {/* Paper Size */}
        <SimpleTooltip content={t('faxDevices:hints.paperSize')} title={t('faxDevices:fields.paperSize')} position="top" maxWidth="400px">
          <Label htmlFor="paperSize" className="text-left">{t('faxDevices:fields.paperSize')}</Label>
        </SimpleTooltip>
        <Select value={formData.paperSize} onValueChange={(v) => onFieldChange('paperSize', v)} options={paperSizeOptions} />

        {/* Storage Path */}
        <SimpleTooltip content={t('faxDevices:hints.storagePath')} title={t('faxDevices:fields.storagePath')} position="top" maxWidth="400px">
          <Label htmlFor="storagePath" className="text-left">{t('faxDevices:fields.storagePath')}</Label>
        </SimpleTooltip>
        <Input id="storagePath" value={formData.storagePath} onChange={(e) => onFieldChange('storagePath', e.target.value)} placeholder={t('faxDevices:placeholders.storagePath')} />

        {/* Auto Delete After Days */}
        <SimpleTooltip content={t('faxDevices:hints.autoDeleteAfterDays')} title={t('faxDevices:fields.autoDeleteAfterDays')} position="top" maxWidth="400px">
          <Label htmlFor="autoDeleteAfterDays" className="text-left">{t('faxDevices:fields.autoDeleteAfterDays')}</Label>
        </SimpleTooltip>
        <Input id="autoDeleteAfterDays" type="number" min="0" value={formData.autoDeleteAfterDays} onChange={(e) => onFieldChange('autoDeleteAfterDays', parseInt(e.target.value) || 0)} />

        {/* T.38 Enabled */}
        <SimpleTooltip content={t('faxDevices:hints.t38Enabled')} title={t('faxDevices:fields.t38Enabled')} position="top" maxWidth="400px">
          <Label htmlFor="t38Enabled" className="text-left">{t('faxDevices:fields.t38Enabled')}</Label>
        </SimpleTooltip>
        <Toggle id="t38Enabled" value={formData.t38Enabled} onChange={(v) => onFieldChange('t38Enabled', v)} />

        {/* ECM Enabled */}
        <SimpleTooltip content={t('faxDevices:hints.ecmEnabled')} title={t('faxDevices:fields.ecmEnabled')} position="top" maxWidth="400px">
          <Label htmlFor="ecmEnabled" className="text-left">{t('faxDevices:fields.ecmEnabled')}</Label>
        </SimpleTooltip>
        <Toggle id="ecmEnabled" value={formData.ecmEnabled} onChange={(v) => onFieldChange('ecmEnabled', v)} />

        {/* Convert to PDF */}
        <SimpleTooltip content={t('faxDevices:hints.convertToPdf')} title={t('faxDevices:fields.convertToPdf')} position="top" maxWidth="400px">
          <Label htmlFor="convertToPdf" className="text-left">{t('faxDevices:fields.convertToPdf')}</Label>
        </SimpleTooltip>
        <Toggle id="convertToPdf" value={formData.convertToPdf} onChange={(v) => onFieldChange('convertToPdf', v)} />

        {/* Fax Station ID */}
        <SimpleTooltip content={t('faxDevices:hints.faxIdent')} title={t('faxDevices:fields.faxIdent')} position="top" maxWidth="400px">
          <Label htmlFor="faxIdent" className="text-left">{t('faxDevices:fields.faxIdent')}</Label>
        </SimpleTooltip>
        <Input id="faxIdent" value={formData.faxIdent} onChange={(e) => onFieldChange('faxIdent', e.target.value)} placeholder={formData.name || 'MyFaxStation'} />

        {/* Fax Header */}
        <SimpleTooltip content={t('faxDevices:hints.faxHeader')} title={t('faxDevices:fields.faxHeader')} position="top" maxWidth="400px">
          <Label htmlFor="faxHeader" className="text-left">{t('faxDevices:fields.faxHeader')}</Label>
        </SimpleTooltip>
        <Input id="faxHeader" value={formData.faxHeader} onChange={(e) => onFieldChange('faxHeader', e.target.value)} placeholder={formData.name || 'MyCompany Fax'} />

      </div>
    </FormBox>
  );
}
